import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Phone, Pill, Bus as BusIcon, ShieldCheck, Users } from 'lucide-react';
import { GUARDIAN_RELATION_LABELS } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate, formatPhone } from '@/lib/format';
import type { ClassRoomSummary } from '@/types';

interface HandoverChild {
  id: string;
  name: string;
  birthDate: string;
  allergies: string[];
  notes: string | null;
  photoConsent: boolean;
  guardians: { name: string; phone: string | null; relation: string; isPrimary: boolean }[];
  pickupPersons: { name: string; relation: string }[];
  activeMedications: {
    id: string;
    date: string;
    medicineType: string;
    symptom: string;
    dosage: string;
    times: string[];
  }[];
  busRoutes: string[];
}

interface HandoverBoard {
  classRoom: { id: string; name: string };
  children: HandoverChild[];
}

/**
 * 인수인계 보드 — §5.27. 연차·대체 근무 때 종이 인수인계서를 대신한다.
 * 평소 쌓인 기록에서 자동으로 만들어지므로 담임이 따로 쓸 것이 없다.
 */
export function HandoverPage() {
  const [classRoomId, setClassRoomId] = useState('');

  const classrooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  useEffect(() => {
    if (!classRoomId && classrooms.data?.length) setClassRoomId(classrooms.data[0].id);
  }, [classrooms.data, classRoomId]);

  const board = useQuery({
    queryKey: ['handover', classRoomId],
    queryFn: () => apiData<HandoverBoard>(`/children/handover?classRoomId=${classRoomId}`),
    enabled: Boolean(classRoomId),
    placeholderData: keepPreviousData,
  });

  return (
    <Screen>
      <AppBar title="인수인계" subtitle="평소 기록으로 자동으로 만들어져요" back />

      <div className="flex flex-col gap-3 p-4">
        <p className="rounded-sm bg-info-soft px-3 py-2 text-sm">
          연차·대체 근무 때 이 화면을 그대로 보여 주세요. 알레르기·비상연락·하원
          담당자·진행 중 투약이 아이별로 정리되어 있어요.
        </p>

        <div className="scroll-x flex gap-2 overflow-x-auto">
          {(classrooms.data ?? []).map((room) => (
            <Chip
              key={room.id}
              tone={classRoomId === room.id ? 'primary' : 'neutral'}
              selected={classRoomId === room.id}
              onClick={() => setClassRoomId(room.id)}
            >
              {room.name}
            </Chip>
          ))}
        </div>

        {board.isPending && classRoomId && <Spinner />}
        {board.isError && (
          <ErrorNote message="불러오지 못했어요." onRetry={() => void board.refetch()} />
        )}

        {board.data?.children.length === 0 && (
          <EmptyState title="이 반에 재원 중인 아이가 없어요" body="반 편성을 확인해 주세요." />
        )}

        {(board.data?.children ?? []).map((child) => (
          <Card key={child.id}>
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="font-semibold">{child.name}</p>
              <span className="text-sm text-ink-sub">{formatDate(child.birthDate)}생</span>
              {!child.photoConsent && <Chip tone="secondary">사진 비공개</Chip>}
            </div>

            {child.allergies.length > 0 && (
              <p className="mt-1.5 flex items-start gap-1.5 rounded-sm bg-secondary-soft px-2.5 py-1.5 text-sm">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                알레르기: <b>{child.allergies.join(', ')}</b>
              </p>
            )}

            {child.notes && (
              <p className="mt-1.5 whitespace-pre-wrap text-sm">
                <b>특이사항</b> — {child.notes}
              </p>
            )}

            <div className="mt-2 flex flex-col gap-1 border-t border-line pt-2 text-sm">
              <p className="flex items-center gap-1.5">
                <Users size={13} className="shrink-0 text-ink-sub" />
                {child.guardians
                  .map(
                    (g) =>
                      `${g.name}(${GUARDIAN_RELATION_LABELS[g.relation as keyof typeof GUARDIAN_RELATION_LABELS] ?? g.relation}${g.isPrimary ? '·대표' : ''})${g.phone ? ` ${formatPhone(g.phone)}` : ''}`,
                  )
                  .join(' / ')}
              </p>
              {child.pickupPersons.length > 0 && (
                <p className="flex items-center gap-1.5">
                  <ShieldCheck size={13} className="shrink-0 text-ink-sub" />
                  하원 담당: {child.pickupPersons.map((p) => `${p.name}(${p.relation})`).join(', ')}
                </p>
              )}
              {child.activeMedications.map((medication) => (
                <p key={medication.id} className="flex items-center gap-1.5">
                  <Pill size={13} className="shrink-0 text-ink-sub" />
                  {formatDate(medication.date)} {medication.medicineType} · {medication.dosage}
                  {medication.times.length > 0 && ` · ${medication.times.join(', ')}`}
                  <span className="text-ink-sub">({medication.symptom})</span>
                </p>
              ))}
              {child.busRoutes.length > 0 && (
                <p className="flex items-center gap-1.5">
                  <BusIcon size={13} className="shrink-0 text-ink-sub" />
                  버스 {child.busRoutes.join(', ')}
                </p>
              )}
              {child.guardians.some((g) => g.phone) && (
                <p className="flex items-center gap-1.5 text-ink-sub">
                  <Phone size={13} className="shrink-0" /> 급할 땐 대표 보호자에게 먼저 연락하세요.
                </p>
              )}
            </div>
          </Card>
        ))}
      </div>
    </Screen>
  );
}
